import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

/**
 * The company's plan state, as the whole app reads it.
 *
 *   active  - an ACTIVE subscription exists (the only state in which the app opens)
 *   pending - the company never had a subscription: waiting for the team to grant one
 *   ended   - it had one and none is active any more (`last_subscription` carries it)
 *
 * Source of truth is /company/details: `has_active_subscription === false`
 * means no plan. A payload without that field (an older cached response) is
 * treated as active, so this can never lock out a customer by mistake.
 */
export function derivePlanState(details) {
  if (!details || details.has_active_subscription !== false) return { status: 'active', last: null };
  const last = details.last_subscription || null;
  return last ? { status: 'ended', last } : { status: 'pending', last: null };
}

export const hasNoPlan = (details) => derivePlanState(details).status !== 'active';

/** The one 402 the subscription gate throws (CheckSubscriptionExpiry). */
export const SUBSCRIPTION_402_MESSAGE = 'Your subscription expired!';
export const isSubscription402 = (error) =>
  error?.response?.status === 402 && error?.response?.data?.message === SUBSCRIPTION_402_MESSAGE;

/**
 * The 402 the feature gate throws (CheckFeature middleware, error 'feature_disabled'):
 * the package simply does not include that feature. It is never worth a red toast -
 * the page-level guards stop the call being made, and anything that slips through
 * is dropped by ToastContext.
 */
export const FEATURE_402_MESSAGE = 'This feature is not included in your current package. Please upgrade.';

// While the no-package state is on screen nothing else may toast: the state IS
// the explanation. A module flag (not redux) because showToast lives in a
// context provider that is outside the store consumers' render path.
let suppressToasts = false;
export const setToastSuppression = (v) => { suppressToasts = !!v; };
export const toastsSuppressed = () => suppressToasts;

export const PLAN_RECHECK_EVENT = 'pp:plan-recheck';

/**
 * The plan state for shell components that render BEFORE redux has the company
 * details (the shell mounts in the same commit the router decides). The
 * react-query cache already holds the /company/details the router waited for, so
 * read that first and fall back to redux.
 */
export function usePlanState() {
  const qc = useQueryClient();
  const redux = useSelector((s) => s.global.companyDetails);
  return derivePlanState(qc.getQueryData('getUserSubscription') ?? redux);
}
